import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plus,
  Search,
  FileText,
  Send,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Printer,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_leadmaster-42/artifacts/0oeobop9_logo%20letra%20transparente.png';

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  paid: 'bg-green-500/20 text-green-400',
  overdue: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-gray-500/20 text-gray-400',
};

// Calculate due date based on payment terms
const calculateDueDate = (paymentTerms) => {
  const today = new Date();
  let daysToAdd = 0;
  
  switch (paymentTerms) {
    case 'due_on_receipt':
      daysToAdd = 0;
      break;
    case 'net_15':
      daysToAdd = 15;
      break;
    case 'net_30':
      daysToAdd = 30;
      break;
    case 'net_45':
      daysToAdd = 45;
      break;
    case 'net_60':
      daysToAdd = 60;
      break;
    default:
      daysToAdd = 30;
  }
  
  today.setDate(today.getDate() + daysToAdd);
  return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [formData, setFormData] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    client_name: '',
    client_email: '',
    client_address: '',
    client_city: '',
    client_state: '',
    client_zip: '',
    client_ein: '',
    due_date: calculateDueDate('net_30'),
    payment_terms: 'net_30',
    notes: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
  });

  // Handler for payment terms change - auto-calculates due date from invoice date
  const handlePaymentTermsChange = (newTerms) => {
    const baseDate = formData.invoice_date || new Date().toISOString().split('T')[0];
    const newDueDate = calculateDueDateFromBase(baseDate, newTerms);
    setFormData(prev => ({
      ...prev,
      payment_terms: newTerms,
      due_date: newDueDate
    }));
  };

  // Handler for invoice date change - recalculates due date
  const handleInvoiceDateChange = (newDate) => {
    const newDueDate = calculateDueDateFromBase(newDate, formData.payment_terms);
    setFormData(prev => ({
      ...prev,
      invoice_date: newDate,
      due_date: newDueDate
    }));
  };

  // Calculate due date from a base date
  const calculateDueDateFromBase = (baseDate, terms) => {
    const date = new Date(baseDate);
    const daysMap = {
      'due_on_receipt': 0,
      'net_15': 15,
      'net_30': 30,
      'net_45': 45,
      'net_60': 60,
    };
    date.setDate(date.getDate() + (daysMap[terms] || 30));
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    if (clientId) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setFormData(prev => ({
          ...prev,
          client_name: client.company_name,
          client_email: client.email,
          client_address: client.address || '',
          client_city: client.city || '',
          client_state: client.state || '',
          client_zip: client.zip_code || '',
          client_ein: client.ein || '',
        }));
      }
    } else {
      // Clear client fields if "manual" is selected
      setFormData(prev => ({
        ...prev,
        client_name: '',
        client_email: '',
        client_address: '',
        client_city: '',
        client_state: '',
        client_zip: '',
        client_ein: '',
      }));
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedInvoice) {
        await axios.put(`${API}/invoices/${selectedInvoice.id}`, formData);
      } else {
        await axios.post(`${API}/invoices`, formData);
      }
      fetchInvoices();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save invoice:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await axios.patch(`${API}/invoices/${id}/status`, { status });
      fetchInvoices();
      if (showViewModal) {
        setSelectedInvoice({ ...selectedInvoice, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_date: new Date().toISOString().split('T')[0],
      client_name: '',
      client_email: '',
      client_address: '',
      client_city: '',
      client_state: '',
      client_zip: '',
      client_ein: '',
      due_date: calculateDueDate('net_30'),
      payment_terms: 'net_30',
      notes: '',
      items: [{ description: '', quantity: 1, rate: 0 }],
    });
    setSelectedInvoice(null);
    setSelectedClientId('');
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, rate: 0 }],
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handlePrintInvoice = () => {
    const printContent = document.getElementById('printable-invoice');
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice #${selectedInvoice?.invoice_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 40px;
            color: #1a1a1a;
            max-width: 800px;
            margin: 0 auto;
          }
          .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
          .logo { height: 50px; margin-bottom: 12px; }
          .invoice-title { font-size: 32px; font-weight: bold; color: #1a1a1a; }
          .invoice-number { color: #666; margin-top: 4px; }
          .company-info { text-align: right; }
          .company-name { font-weight: bold; font-size: 18px; }
          .company-detail { color: #666; font-size: 14px; margin-top: 2px; }
          .billing-section { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px; }
          .bill-to-label { color: #888; font-size: 12px; text-transform: uppercase; margin-bottom: 8px; }
          .client-name { font-weight: 600; font-size: 16px; }
          .client-detail { color: #666; font-size: 14px; margin-top: 2px; }
          .invoice-meta { text-align: right; }
          .meta-row { font-size: 14px; margin-bottom: 4px; }
          .meta-label { color: #888; }
          .status-badge { 
            display: inline-block; 
            padding: 4px 12px; 
            border-radius: 20px; 
            font-size: 12px; 
            font-weight: 600; 
            text-transform: uppercase;
            margin-top: 8px;
          }
          .status-paid { background: #d1fae5; color: #065f46; }
          .status-pending { background: #fef3c7; color: #92400e; }
          .status-overdue { background: #fee2e2; color: #991b1b; }
          .status-draft { background: #f3f4f6; color: #4b5563; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { 
            text-align: left; 
            padding: 12px 0; 
            border-bottom: 2px solid #e5e7eb; 
            font-size: 12px;
            text-transform: uppercase;
            color: #666;
            font-weight: 600;
          }
          th:nth-child(2), th:nth-child(3), th:nth-child(4) { text-align: right; }
          td { 
            padding: 16px 0; 
            border-bottom: 1px solid #f3f4f6; 
            font-size: 14px;
          }
          td:nth-child(2), td:nth-child(3), td:nth-child(4) { text-align: right; }
          .total-section { display: flex; justify-content: flex-end; margin-bottom: 30px; }
          .total-box { width: 250px; }
          .total-row { 
            display: flex; 
            justify-content: space-between; 
            padding: 12px 0; 
            border-top: 2px solid #1a1a1a;
          }
          .total-label { font-weight: bold; font-size: 16px; }
          .total-amount { font-weight: bold; font-size: 20px; }
          .notes-section { 
            background: #f9fafb; 
            padding: 20px; 
            border-radius: 8px;
            margin-bottom: 30px;
          }
          .notes-label { font-size: 12px; color: #888; text-transform: uppercase; margin-bottom: 8px; }
          .notes-text { font-size: 14px; color: #4b5563; }
          .footer { 
            text-align: center; 
            padding-top: 30px; 
            border-top: 1px solid #e5e7eb;
            color: #888;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            @page { margin: 0.5in; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    pending: invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + (i.total || 0), 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0),
    overdue: invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + (i.total || 0), 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-white font-medium">Invoices</h1>
          <p className="text-gray-400">Manage your client invoices</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Invoice
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#1a1a1f] rounded-xl border border-white/5 p-4">
          <p className="text-gray-400 text-sm">Total Invoiced</p>
          <p className="text-2xl text-white font-bold">{formatCurrency(stats.total)}</p>
        </div>
        <div className="bg-[#1a1a1f] rounded-xl border border-white/5 p-4">
          <p className="text-yellow-400 text-sm">Pending</p>
          <p className="text-2xl text-white font-bold">{formatCurrency(stats.pending)}</p>
        </div>
        <div className="bg-[#1a1a1f] rounded-xl border border-white/5 p-4">
          <p className="text-green-400 text-sm">Paid</p>
          <p className="text-2xl text-white font-bold">{formatCurrency(stats.paid)}</p>
        </div>
        <div className="bg-[#1a1a1f] rounded-xl border border-white/5 p-4">
          <p className="text-red-400 text-sm">Overdue</p>
          <p className="text-2xl text-white font-bold">{formatCurrency(stats.overdue)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'draft', 'pending', 'paid', 'overdue'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-purple-600 text-white'
                  : 'bg-[#1a1a1f] text-gray-400 hover:text-white border border-white/10'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No invoices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Invoice #</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Client</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Amount</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Status</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Invoice Date</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Due Date</th>
                  <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-white font-medium">#{invoice.invoice_number}</td>
                    <td className="px-6 py-4">
                      <div className="text-white">{invoice.client_name}</div>
                      <div className="text-gray-500 text-sm">{invoice.client_email}</div>
                    </td>
                    <td className="px-6 py-4 text-white font-medium">{formatCurrency(invoice.total)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[invoice.status]}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400">{formatDate(invoice.invoice_date || invoice.created_at)}</td>
                    <td className="px-6 py-4 text-gray-400">{formatDate(invoice.due_date)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setSelectedInvoice(invoice); setShowViewModal(true); }}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setFormData({
                              ...invoice,
                              invoice_date: invoice.invoice_date || invoice.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
                              items: invoice.items || [{ description: '', quantity: 1, rate: 0 }],
                            });
                            setShowModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(invoice.id)}
                          className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="min-h-full flex items-start sm:items-center justify-center p-2 sm:p-4">
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowModal(false)} />
            <div className="relative w-full max-w-3xl bg-[#1a1a1f] rounded-2xl sm:rounded-3xl border border-white/10 p-4 sm:p-8 my-2 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white z-10"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              <h2 className="text-lg sm:text-xl text-white font-medium mb-4 sm:mb-6 pr-8">
                {selectedInvoice ? 'Edit Invoice' : 'Create New Invoice'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Client Information */}
                <div className="bg-[#0a0a0b] rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <h3 className="text-white font-medium text-sm sm:text-base">Client Information</h3>
                    {clients.length > 0 && (
                    <select
                      value={selectedClientId}
                      onChange={(e) => handleClientSelect(e.target.value)}
                      className="px-3 py-2 bg-[#1a1a1f] border border-white/10 rounded-lg text-white text-sm w-full sm:w-auto"
                    >
                      <option value="">Select saved client...</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.company_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Client Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Client Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">City</label>
                    <input
                      type="text"
                      value={formData.client_city}
                      onChange={(e) => setFormData({ ...formData, client_city: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">State</label>
                    <input
                      type="text"
                      value={formData.client_state}
                      onChange={(e) => setFormData({ ...formData, client_state: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">ZIP Code</label>
                    <input
                      type="text"
                      value={formData.client_zip}
                      onChange={(e) => setFormData({ ...formData, client_zip: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Client EIN/Tax ID (optional)</label>
                  <input
                    type="text"
                    value={formData.client_ein}
                    onChange={(e) => setFormData({ ...formData, client_ein: e.target.value })}
                    placeholder="XX-XXXXXXX"
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                  />
                </div>
              </div>

              {/* Invoice Details */}
              <div className="bg-[#0a0a0b] rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                <h3 className="text-white font-medium text-sm sm:text-base">Invoice Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Invoice Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.invoice_date}
                      onChange={(e) => handleInvoiceDateChange(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                      data-testid="invoice-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Due Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Payment Terms</label>
                    <select
                      value={formData.payment_terms}
                      onChange={(e) => handlePaymentTermsChange(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                    >
                      <option value="due_on_receipt">Due on Receipt</option>
                      <option value="net_15">Net 15</option>
                      <option value="net_30">Net 30</option>
                      <option value="net_45">Net 45</option>
                      <option value="net_60">Net 60</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="bg-[#0a0a0b] rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-medium text-sm sm:text-base">Line Items</h3>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-purple-400 text-sm hover:underline"
                  >
                    + Add Item
                  </button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="space-y-2 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-4 sm:items-end border-b border-white/5 pb-3 sm:pb-0 sm:border-0">
                    <div className="sm:col-span-5">
                      <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:contents">
                      <div className="sm:col-span-2">
                        <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Rate ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm sm:text-base"
                        />
                      </div>
                      <div className="sm:col-span-2 flex items-center justify-between">
                        <span className="text-white text-sm sm:text-base">{formatCurrency(item.quantity * item.rate)}</span>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-3 sm:pt-4 border-t border-white/10">
                  <div className="text-right">
                    <span className="text-gray-400 text-sm">Total: </span>
                    <span className="text-xl sm:text-2xl text-white font-bold">{formatCurrency(calculateTotal(formData.items))}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-gray-400 text-sm mb-1 sm:mb-2">Notes / Terms</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Payment instructions, terms, or additional notes..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white resize-none text-sm sm:text-base"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="w-full sm:flex-1 py-2.5 sm:py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white transition-colors text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:flex-1 py-2.5 sm:py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors text-sm sm:text-base"
                >
                  {selectedInvoice ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowViewModal(false)} />
          <div className="relative w-full max-w-3xl bg-[#1a1a1f] rounded-3xl border border-white/10 my-8 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-600/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl text-white font-medium">Invoice #{selectedInvoice.invoice_number}</h2>
                  <p className="text-gray-400 text-sm">Date: {formatDate(selectedInvoice.invoice_date || selectedInvoice.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  selectedInvoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                  selectedInvoice.status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                  selectedInvoice.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {selectedInvoice.status?.toUpperCase()}
                </span>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Printable Invoice Content */}
            <div className="p-8 bg-white" id="printable-invoice">
              <div className="invoice-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                  <img src={LOGO_URL} alt="Ruonn" style={{ height: '50px', marginBottom: '12px' }} />
                  <div className="invoice-title" style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a1a1a' }}>INVOICE</div>
                  <div className="invoice-number" style={{ color: '#666', marginTop: '4px' }}>#{selectedInvoice.invoice_number}</div>
                </div>
                <div className="company-info" style={{ textAlign: 'right' }}>
                  <div className="company-name" style={{ fontWeight: 'bold', fontSize: '18px', color: '#1a1a1a' }}>
                    {companySettings?.company_name || 'Ruonn LLC'}
                  </div>
                  <div className="company-detail" style={{ color: '#666', fontSize: '14px', marginTop: '2px' }}>
                    {companySettings?.company_address || '123 Business St'}
                  </div>
                  <div className="company-detail" style={{ color: '#666', fontSize: '14px' }}>
                    {companySettings?.company_city || 'Miami'}, {companySettings?.company_state || 'FL'} {companySettings?.company_zip || '33101'}
                  </div>
                  {companySettings?.company_phone && (
                    <div className="company-detail" style={{ color: '#666', fontSize: '14px' }}>
                      {companySettings.company_phone}
                    </div>
                  )}
                  {companySettings?.company_email && (
                    <div className="company-detail" style={{ color: '#666', fontSize: '14px' }}>
                      {companySettings.company_email}
                    </div>
                  )}
                  <div className="company-detail" style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
                    EIN: {companySettings?.company_ein || 'XX-XXXXXXX'}
                  </div>
                </div>
              </div>

              <div className="billing-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px' }}>
                <div>
                  <div className="bill-to-label" style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>Bill To</div>
                  <div className="client-name" style={{ fontWeight: '600', fontSize: '16px', color: '#1a1a1a' }}>{selectedInvoice.client_name}</div>
                  <div className="client-detail" style={{ color: '#666', fontSize: '14px', marginTop: '2px' }}>{selectedInvoice.client_email}</div>
                  {selectedInvoice.client_address && (
                    <div className="client-detail" style={{ color: '#666', fontSize: '14px' }}>{selectedInvoice.client_address}</div>
                  )}
                  {selectedInvoice.client_city && (
                    <div className="client-detail" style={{ color: '#666', fontSize: '14px' }}>
                      {selectedInvoice.client_city}, {selectedInvoice.client_state} {selectedInvoice.client_zip}
                    </div>
                  )}
                  {selectedInvoice.client_ein && (
                    <div className="client-detail" style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>EIN: {selectedInvoice.client_ein}</div>
                  )}
                </div>
                <div className="invoice-meta" style={{ textAlign: 'right' }}>
                  <div className="meta-row" style={{ fontSize: '14px', marginBottom: '4px' }}>
                    <span className="meta-label" style={{ color: '#888' }}>Invoice Date: </span>
                    <span style={{ color: '#1a1a1a', fontWeight: '500' }}>{formatDate(selectedInvoice.invoice_date || selectedInvoice.created_at)}</span>
                  </div>
                  <div className="meta-row" style={{ fontSize: '14px', marginBottom: '4px' }}>
                    <span className="meta-label" style={{ color: '#888' }}>Due Date: </span>
                    <span style={{ color: '#1a1a1a', fontWeight: '500' }}>{formatDate(selectedInvoice.due_date)}</span>
                  </div>
                  <div className="meta-row" style={{ fontSize: '14px' }}>
                    <span className="meta-label" style={{ color: '#888' }}>Payment Terms: </span>
                    <span style={{ color: '#1a1a1a', fontWeight: '500' }}>{selectedInvoice.payment_terms?.replace('_', ' ').toUpperCase()}</span>
                  </div>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '12px 0', fontSize: '12px', textTransform: 'uppercase', color: '#666', fontWeight: '600' }}>Description</th>
                    <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '12px', textTransform: 'uppercase', color: '#666', fontWeight: '600' }}>Qty</th>
                    <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '12px', textTransform: 'uppercase', color: '#666', fontWeight: '600' }}>Rate</th>
                    <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '12px', textTransform: 'uppercase', color: '#666', fontWeight: '600' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items?.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '16px 0', fontSize: '14px', color: '#1a1a1a' }}>{item.description}</td>
                      <td style={{ padding: '16px 0', fontSize: '14px', color: '#1a1a1a', textAlign: 'right' }}>{item.quantity}</td>
                      <td style={{ padding: '16px 0', fontSize: '14px', color: '#1a1a1a', textAlign: 'right' }}>{formatCurrency(item.rate)}</td>
                      <td style={{ padding: '16px 0', fontSize: '14px', color: '#1a1a1a', textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.quantity * item.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
                <div style={{ width: '250px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', borderTop: '2px solid #1a1a1a' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#1a1a1a' }}>Total USD</span>
                    <span style={{ fontWeight: 'bold', fontSize: '22px', color: '#1a1a1a' }}>{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>
              </div>

              {(selectedInvoice.notes || companySettings?.payment_instructions) && (
                <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '8px' }}>
                  {selectedInvoice.notes && (
                    <div style={{ marginBottom: companySettings?.payment_instructions ? '16px' : '0' }}>
                      <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>Notes</div>
                      <div style={{ fontSize: '14px', color: '#4b5563' }}>{selectedInvoice.notes}</div>
                    </div>
                  )}
                  {companySettings?.payment_instructions && (
                    <div>
                      <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>Payment Instructions</div>
                      <div style={{ fontSize: '14px', color: '#4b5563', whiteSpace: 'pre-line' }}>{companySettings.payment_instructions}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex gap-4 px-8 py-5 border-t border-white/10 bg-[#0a0a0b]">
              {selectedInvoice.status !== 'paid' && (
                <button
                  onClick={() => handleStatusUpdate(selectedInvoice.id, 'paid')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium transition-colors"
                >
                  <CheckCircle className="w-5 h-5" />
                  Mark as Paid
                </button>
              )}
              {selectedInvoice.status === 'paid' && (
                <button
                  onClick={() => handleStatusUpdate(selectedInvoice.id, 'pending')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-yellow-600 hover:bg-yellow-700 text-white font-medium transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  Mark as Pending
                </button>
              )}
              <button
                onClick={handlePrintInvoice}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors"
              >
                <Printer className="w-5 h-5" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
